import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, X, Star } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const FUELS = ["Petrol", "Diesel", "Hybrid", "Electric"];
const TRANSMISSIONS = ["Manual", "Automatic"];
const STATUSES = ["AVAILABLE", "RESERVED", "SOLD", "HIDDEN"];

const EMPTY = {
  title: "", price: "", make: "", model: "", year: "", mileage: "", fuel: "", transmission: "",
  description: "", status: "AVAILABLE", images: [], main_image: 0,
};

const lbl = "text-xs font-heading font-bold uppercase tracking-wider text-pale/60";
const inp = "bg-navy border-deep text-white h-11 mt-1.5";

export default function CarForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      api.get("/admin/cars").then((res) => {
        const c = res.data.find((x) => x.id === id);
        if (c) setForm({ ...EMPTY, ...c });
        else toast.error("Car not found");
      });
    }
  }, [id, isEdit]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const data = new FormData();
      files.forEach((f) => data.append("files", f));
      const res = await api.post("/admin/uploads", data, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, ...res.data.map((r) => r.path)] }));
      toast.success(`${res.data.length} photo(s) uploaded.`);
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (i) => {
    setForm((f) => {
      const images = f.images.filter((_, idx) => idx !== i);
      let main = f.main_image;
      if (i === main) main = 0;
      else if (i < main) main -= 1;
      return { ...f, images, main_image: Math.min(main, Math.max(images.length - 1, 0)) };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0 };
      if (!payload.title) payload.title = [payload.year, payload.make, payload.model].filter(Boolean).join(" ");
      if (isEdit) await api.put(`/admin/cars/${id}`, payload);
      else await api.post("/admin/cars", payload);
      toast.success(isEdit ? "Car updated." : "Car listed for sale.");
      navigate("/admin/cars");
    } catch (err) {
      toast.error("Could not save car.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="car-form-page" className="max-w-3xl">
      <Link to="/admin/cars" className="inline-flex items-center gap-2 text-sm text-pale/60 hover:text-sky transition-colors mb-5">
        <ArrowLeft size={15} /> Back to cars
      </Link>
      <h1 className="font-heading font-extrabold text-white text-2xl tracking-tight">{isEdit ? "Edit Car" : "Add Car For Sale"}</h1>

      <form data-testid="car-form" onSubmit={submit} className="mt-6 space-y-5">
        <div className="bg-navy border border-deep rounded-lg p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className={lbl}>Listing Title *</Label>
            <Input data-testid="cf-title" required value={form.title} onChange={set("title")} className={inp} placeholder="e.g. Ford Fiesta ST-Line 1.0 EcoBoost" />
          </div>
          <div>
            <Label className={lbl}>Price (£) *</Label>
            <Input data-testid="cf-price" required type="number" min="0" step="1" value={form.price} onChange={set("price")} className={inp} />
          </div>
          <div>
            <Label className={lbl}>Year</Label>
            <Input data-testid="cf-year" value={form.year} onChange={set("year")} className={inp} placeholder="e.g. 2019" />
          </div>
          <div>
            <Label className={lbl}>Make</Label>
            <Input data-testid="cf-make" value={form.make} onChange={set("make")} className={inp} placeholder="e.g. Ford" />
          </div>
          <div>
            <Label className={lbl}>Model</Label>
            <Input data-testid="cf-model" value={form.model} onChange={set("model")} className={inp} placeholder="e.g. Fiesta" />
          </div>
          <div>
            <Label className={lbl}>Mileage</Label>
            <Input data-testid="cf-mileage" value={form.mileage} onChange={set("mileage")} className={inp} placeholder="e.g. 42,000 miles" />
          </div>
          <div>
            <Label className={lbl}>Fuel</Label>
            <Select value={form.fuel} onValueChange={set("fuel")}>
              <SelectTrigger data-testid="cf-fuel" className={`${inp} w-full`}><SelectValue placeholder="Select fuel" /></SelectTrigger>
              <SelectContent>{FUELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className={lbl}>Transmission</Label>
            <Select value={form.transmission} onValueChange={set("transmission")}>
              <SelectTrigger data-testid="cf-transmission" className={`${inp} w-full`}><SelectValue placeholder="Select transmission" /></SelectTrigger>
              <SelectContent>{TRANSMISSIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className={lbl}>Status</Label>
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="cf-status" className={`${inp} w-full`}><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className={lbl}>Description</Label>
            <Textarea data-testid="cf-description" rows={5} value={form.description} onChange={set("description")} className="bg-navy border-deep text-white mt-1.5" placeholder="Condition, service history, MOT expiry, features, number of owners..." />
          </div>
        </div>

        <div className="bg-navy border border-deep rounded-lg p-6">
          <Label className={lbl}>Photos</Label>
          <div className="flex flex-wrap gap-3 mt-3">
            {form.images.map((img, i) => (
              <div key={i} data-testid={`cf-image-${i}`} className={`relative w-24 h-24 rounded-md overflow-hidden border-2 ${i === form.main_image ? "border-sky" : "border-deep"}`}>
                <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} data-testid={`cf-remove-image-${i}`} className="absolute top-1 right-1 bg-navy/90 text-white rounded-full p-1 hover:bg-red-900"><X size={12} /></button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, main_image: i })}
                  data-testid={`cf-main-image-${i}`}
                  title="Set as main image"
                  className={`absolute bottom-1 left-1 rounded-full p-1 ${i === form.main_image ? "bg-sky text-white" : "bg-navy/90 text-pale/70 hover:text-white"}`}
                >
                  <Star size={12} fill={i === form.main_image ? "currentColor" : "none"} />
                </button>
              </div>
            ))}
            <label data-testid="cf-upload-label" className="w-24 h-24 rounded-md border-2 border-dashed border-deep hover:border-sky/50 flex flex-col items-center justify-center gap-1 cursor-pointer text-pale/50 hover:text-sky transition-colors">
              <Upload size={18} />
              <span className="text-[10px] font-bold uppercase tracking-wide">{uploading ? "..." : "Upload"}</span>
              <input type="file" accept="image/*" multiple data-testid="cf-upload-input" className="hidden" onChange={uploadFiles} disabled={uploading} />
            </label>
          </div>
          <p className="text-xs text-pale/40 mt-3">Upload one or more photos. Click the star to choose the main listing image.</p>
        </div>

        <div className="flex gap-3">
          <button type="submit" data-testid="cf-save-btn" disabled={busy || uploading} className="btn-primary disabled:opacity-60">
            {busy ? "SAVING..." : isEdit ? "SAVE CHANGES" : "ADD CAR"}
          </button>
          <Link to="/admin/cars" className="inline-flex items-center px-6 py-3 rounded-md border border-deep text-pale/70 hover:text-white font-heading font-bold tracking-wide text-sm transition-colors">CANCEL</Link>
        </div>
      </form>
    </div>
  );
}
